import { AlertTriangle, CalendarCheck, Wallet, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import { QueryError } from '@/components/ui/QueryError';
import { Skeleton } from '@/components/ui/Skeleton';
import type { ApiQueryState } from '@/hooks/useApiQuery';
import { formatCurrency, formatNumber } from '@/lib/format';
import type { PlatformStats, SubscriptionIncome } from '@/types/institute';

interface SubscriptionIncomeStripProps {
  stats: PlatformStats | null;
  isLoading: boolean;
  /** Fetched once by the dashboard and shared with the revenue panel below. */
  income: ApiQueryState<SubscriptionIncome>;
}

export function SubscriptionIncomeStrip({
  stats,
  isLoading,
  income,
}: SubscriptionIncomeStripProps) {
  if (income.error) {
    return (
      <QueryError
        title="Could not load this month's income"
        error={income.error}
        onRetry={income.refetch}
      />
    );
  }

  const data = income.data;
  const subscription = stats?.subscription;
  // Only the unpaid customers with a fee agreed: one with no fee owes nothing,
  // so it does not belong beside a rupee figure.
  const owingCount = subscription?.unpaidWithFeeCount ?? 0;
  const dueCount = data?.dueFromLastMonth.instituteCount ?? 0;

  return (
    <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-3">
      {/* Card 1: received this month — opens the ledger from the 1st. The month
          is the server's (India's), not the reader's device clock. */}
      <Link
        to={data ? `/platform/subscriptions?from=${data.month}` : '/platform/subscriptions'}
        className="group flex flex-col justify-between rounded-2xl border border-emerald-300/80 bg-[#ecfdf5] dark:border-emerald-900/80 dark:bg-emerald-950/30 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all hover:shadow-xs"
      >
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-bold text-emerald-800 dark:text-emerald-300">
              Received this month
            </span>
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-200/80 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 shadow-2xs">
              <Wallet className="h-4 w-4" />
            </span>
          </div>
          {income.isLoading ? (
            <Skeleton className="mt-2 h-7 w-24" />
          ) : (
            <p className=" text-[26px] font-black text-emerald-950 dark:text-emerald-200 tracking-tight">
              {data ? formatCurrency(data.thisMonth.amount) : '—'}
            </p>
          )}
        </div>
        <div className=" flex items-center justify-between text-[11.5px] font-semibold text-emerald-700 dark:text-emerald-400">
          <span>
            {data
              ? `Received from ${formatNumber(data.thisMonth.instituteCount)} institute${data.thisMonth.instituteCount === 1 ? '' : 's'}`
              : '—'}
          </span>
          <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </div>
      </Link>

      {/* Card 2: Unpaid income */}
      <div className="flex flex-col justify-between rounded-2xl border border-amber-300/80 bg-[#fffbeb] dark:border-amber-900/80 dark:bg-amber-950/30 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all hover:shadow-xs">
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[13px] font-bold text-amber-800 dark:text-amber-300">
              Unpaid income
            </span>
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-amber-200/80 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300 shadow-2xs">
              <CalendarCheck className="h-4 w-4" />
            </span>
          </div>
          {isLoading ? (
            <Skeleton className="mt-2 h-7 w-24" />
          ) : (
            <p className=" text-[26px] font-black text-amber-950 dark:text-amber-200 tracking-tight">
              {formatCurrency(subscription?.unpaidAmount ?? '0')}
            </p>
          )}
        </div>
        <p className=" text-[11.5px] font-semibold text-amber-700 dark:text-amber-400">
          Agreed fees not yet paid · {owingCount} {owingCount === 1 ? 'institute' : 'institutes'}
        </p>
      </div>

      {/* Card 3: overdue — unpaid institutes whose due date is before today (IST).
          The list it opens has no payment filter, so the link says only that it
          opens the institutes, not that it shows these ones. */}
      <Link
        to="/platform/institutes"
        className="group flex flex-col justify-between rounded-2xl border border-blue-300/80 bg-[#eff6ff] dark:border-blue-900/80 dark:bg-blue-950/30 p-4 shadow-[0_2px_8px_rgba(0,0,0,0.03)] transition-all hover:shadow-xs"
      >
        <div>
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1 text-[13px] font-bold text-blue-800 dark:text-blue-300">
              Overdue
            </span>
            <span className="grid h-7 w-7 place-items-center rounded-lg bg-blue-200/80 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300 shadow-2xs">
              <AlertTriangle className="h-4 w-4" />
            </span>
          </div>
          {income.isLoading ? (
            <Skeleton className="mt-2 h-7 w-24" />
          ) : (
            <p className=" text-[26px] font-black text-blue-950 dark:text-blue-200 tracking-tight">
              {data ? formatCurrency(data.dueFromLastMonth.amount) : '—'}
            </p>
          )}
        </div>
        <div className=" flex items-center justify-between text-[11.5px] font-semibold text-blue-700 dark:text-blue-400">
          <span>
            {!data
              ? '—'
              : dueCount > 0
                ? `${dueCount} institute${dueCount === 1 ? '' : 's'} past due`
                : 'Nothing overdue'}
          </span>
          <span className="flex shrink-0 items-center gap-0.5">
            View institutes
            <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </Link>
    </div>
  );
}
